import { yupResolver } from "@hookform/resolvers/yup";
import { Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import type { EditableTask, Task } from "../../../api/src/tasks/tasks.service";
import Loading from "../Loading";
import { CrmUsersRecord, useQueryCrmUsers } from "../firm/lib";
import { InlineError } from "../lib/display";
import {
  getSchemaFieldFormLabel,
  optionalStringValueTransform,
} from "../lib/forms";
import FormFieldError from "./FormFieldError";
import SubmitButton from "./SubmitButton";
import { useCreateTaskSubmit } from "./useCreateTaskSubmit";

/**
 * What a Slant task can be created with, as against `EditableTask`, which is
 * the shared shape the three CRMs are written from.
 *
 * There is no `category`: Slant tasks have none, and `getTaskCategories`
 * contributes nothing for Slant (s-slant-tasks-capability-implementation-019ed0eb).
 * It is absent rather than hidden, so it cannot reject a submission from a
 * control that is not on screen.
 *
 * `assignedTo` is a Slant user id, which is an opaque string rather than the
 * number Wealthbox and Redtail use. It stays optional because Slant assigns the
 * task to the connected user when `assigned_to_id` is omitted, which is a
 * reasonable default; when the advisor does pick someone, `taskBody`
 * (api/src/slant/tasks.service.ts) sends it
 * (s-task-assignee-list-source-019fd301825de871).
 */
export type EditableSlantTask = Omit<
  EditableTask,
  "category" | "assignedTo"
> & {
  assignedTo?: string;
};

/**
 * An empty Priority or Due Date control has to submit `null` rather than
 * `undefined`: `EditableTask` picks both fields off `Task`, where each is now
 * declared `| null` rather than optional (APP-1539). `forms.ts`'s shared
 * `optionalStringValueTransform` yields `undefined`, which those fields no
 * longer accept, so the empty case is transformed here instead. The twin of the
 * same helper in `CreateTask.tsx`; each form keeps its own so neither depends on
 * the other.
 */
function nullableValueTransform<TValue>(value: TValue, originalValue: string) {
  return originalValue === "" ? null : value;
}

export const slantTaskSchema: yup.ObjectSchema<EditableSlantTask> = yup.object({
  name: yup.string().required().label("Name"),
  description: yup.string().label("Description"),
  // Slant is the only CRM with an Urgent level; acPriorityToSlant maps it to
  // urgent_priority.
  priority: yup
    .string()
    .defined()
    .nullable()
    .default(null)
    .transform(nullableValueTransform)
    .oneOf(["Low", "Medium", "High", "Urgent", null])
    .label("Priority"),
  dueDate: yup
    .date()
    .defined()
    .nullable()
    .default(null)
    .transform(nullableValueTransform)
    .label("Due Date"),
  // Kept as a string, unconverted: a Slant user id is opaque and coercing it to
  // a number would destroy it.
  assignedTo: yup
    .string()
    .transform(optionalStringValueTransform)
    .label("Assignee"),
});

/**
 * The create-task form for a record linked to Slant alone. Wealthbox and
 * Redtail keep the shared `CreateTask` form; the field sets differ enough that
 * one conditional schema could not serve all three
 * (s-slant-create-task-form-019fd301b735a8cd).
 *
 * Its assignee list comes from Slant, via the firm's resolved CRM
 * (s-task-assignee-list-source-019fd301825de871), and a picked assignee is sent
 * on as `assigned_to_id`. The ids are Slant's opaque strings, so nothing here
 * coerces them to numbers.
 */
const CreateSlantTask = ({
  onCreate,
  record,
}: {
  onCreate: (data: EditableSlantTask) => Promise<Task | null>;
  record?: CrmUsersRecord;
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<EditableSlantTask>({
    mode: "onBlur",
    resolver: yupResolver(slantTaskSchema),
  });

  const { errorMessage, submit } = useCreateTaskSubmit({ onCreate, reset });

  const {
    isPending: isPendingAssignees,
    isError: isErrorAssignees,
    data: assigneesData,
    // Scoped to the record this form sits on, so the server reads that record's
    // own CRM rather than deriving the firm's — which on a firm with more than
    // one CRM connected could be a different one entirely
    // (s-task-assignee-list-source-019fd301825de871, R1).
  } = useQueryCrmUsers({ record });

  const assignees = assigneesData?.data ?? [];

  // A connected CRM always has at least one user — the connected user — so an
  // empty list is a fault, not a firm with no staff: the CRM call failed, its
  // token was revoked, or it is down. Rendering an empty dropdown would hide an
  // outage behind a form that looks fine, so the fault is named and the form
  // withheld. Refusing is acceptable here only because the advisor is told why;
  // the silence this component exists to fix is not
  // (s-task-assignee-list-source-019fd301825de871, R9).
  const assigneesUnavailable = isErrorAssignees || assignees.length === 0;

  const assigneeOptions = assignees
    .slice()
    .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
    .map((assignee) => (
      <option key={assignee.id} value={assignee.id}>
        {assignee.name}
      </option>
    ));

  if (isPendingAssignees) {
    return <Loading />;
  }

  if (assigneesUnavailable) {
    return (
      <InlineError>
        Assignees could not be loaded, so a task cannot be created right now.
        Check that the CRM connection is still valid.
      </InlineError>
    );
  }

  return (
    <Form onSubmit={handleSubmit((data) => submit(data))}>
      {errorMessage}
      <Form.Group className="mb-3" controlId="form-name">
        <Form.Label>
          {getSchemaFieldFormLabel(slantTaskSchema.fields.name)}
        </Form.Label>
        <Form.Control type="text" {...register("name")} />
        <FormFieldError field={errors.name} />
      </Form.Group>
      <Form.Group className="mb-3" controlId="form-description">
        <Form.Label>
          {getSchemaFieldFormLabel(slantTaskSchema.fields.description)}
        </Form.Label>
        <Form.Control type="text" {...register("description")} />
        <FormFieldError field={errors.description} />
      </Form.Group>
      <Form.Group className="mb-3" controlId="form-dueDate">
        <Form.Label>
          {getSchemaFieldFormLabel(slantTaskSchema.fields.dueDate)}
        </Form.Label>
        <Form.Control type="date" {...register("dueDate")} />
        <FormFieldError field={errors.dueDate} />
      </Form.Group>
      <Form.Group className="mb-3" controlId="form-assignee">
        <Form.Label>
          {getSchemaFieldFormLabel(slantTaskSchema.fields.assignedTo)}
        </Form.Label>
        <Form.Select {...register("assignedTo")}>
          <option value="">Select an assignee...</option>
          {assigneeOptions}
        </Form.Select>
        <FormFieldError field={errors.assignedTo} />
      </Form.Group>
      <Form.Group className="mb-3" controlId="form-priority">
        <Form.Label>
          {getSchemaFieldFormLabel(slantTaskSchema.fields.priority)}
        </Form.Label>
        <Form.Select {...register("priority")}>
          <option value="">Select a priority...</option>
          <option value="Low">Low</option>
          <option value="Medium">Medium</option>
          <option value="High">High</option>
          <option value="Urgent">Urgent</option>
        </Form.Select>
        <FormFieldError field={errors.priority} />
      </Form.Group>
      <SubmitButton isSubmitting={isSubmitting} label="Create" />
    </Form>
  );
};

export default CreateSlantTask;
